/**
 * Сессия трекера: токены, пользователь и его права.
 *
 * Права приходят готовыми в `/me` — считать их из ролей на фронте не нужно и
 * вредно: список ролей меняется реформами доступов, а `permissions` уже учли
 * и роли, и личные гранты.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { authApi } from './api'
import { clearTokens, readTokens, writeTokens } from './tokens'
import type { User } from './types'

export const useAuthStore = defineStore('auth', () => {
  const stored = readTokens()

  const accessToken = ref<string | null>(stored.accessToken)
  const refreshToken = ref<string | null>(stored.refreshToken)
  const user = ref<User | null>(null)
  const isLoading = ref(false)
  /** Своё фото — имя файла в бакете; пусто у тех, кто его не загружал */
  const avatarFile = ref('')

  const isAuthenticated = computed(() => !!accessToken.value && !!refreshToken.value)

  const permissions = computed(() => new Set(user.value?.permissions ?? []))

  /** Есть ли право вида `tracker:devops`. */
  const can = (permission: string) => permissions.value.has(permission)

  const setTokens = (tokens: { accessToken: string; refreshToken: string }) => {
    accessToken.value = tokens.accessToken
    refreshToken.value = tokens.refreshToken
    writeTokens(tokens)
  }

  const login = async (email: string, password: string) => {
    isLoading.value = true

    try {
      const response = await authApi.login(email, password)

      if (!response.tokens?.accessToken || !response.tokens.refreshToken) {
        throw new Error('Malformed login response')
      }

      setTokens(response.tokens)
      // Пользователь приходит вместе с токенами, но права в этом ответе может
      // не быть — за полной картиной идём в /me.
      user.value = response.user ?? null
      await loadUser()
    } finally {
      isLoading.value = false
    }
  }

  const loadUser = async () => {
    // Фото — соседним запросом: `/me` про настройки профиля не знает, а рисуется
    // оно там же, где имя. Оба ответа нужны экрану вместе, поэтому и идут разом.
    const [me, avatar] = await Promise.all([authApi.me(), authApi.myAvatar()])

    user.value = me
    avatarFile.value = avatar
  }

  const reset = () => {
    accessToken.value = null
    refreshToken.value = null
    user.value = null
    avatarFile.value = ''
    clearTokens()
  }

  const logout = async () => {
    try {
      await authApi.logout()
    } catch {
      // Сервер мог уже забыть сессию — для клиента выход всё равно состоялся.
    } finally {
      reset()
    }
  }

  return {
    accessToken,
    refreshToken,
    user,
    avatarFile,
    isLoading,
    isAuthenticated,
    can,
    setTokens,
    login,
    loadUser,
    logout,
    reset,
  }
})
